<script setup>
const props = defineProps({
  salidas: { type: Array, default: () => [] },
  conductores: { type: Array, default: () => [] },
  grupos: { type: Array, default: () => [] },
  zonas: { type: Array, default: () => [] },
})
defineEmits(['select'])

const getConductorName = (id) => {
  const found = props.conductores?.find((c) => c.id === id)
  return found ? found.name : (id || '')
}

const getGruposStr = (grupoIds) => {
  if (!grupoIds?.length) return ''
  return grupoIds
    .map((gId) => {
      const found = props.grupos?.find((g) => g.id === gId)
      const name = found ? found.name : gId
      const zona = found?.zonaId
        ? props.zonas?.find((z) => z.id === found.zonaId)?.name
        : null
      return zona ? `${name} — ${zona}` : name
    })
    .join(', ')
}

const getLocalDate = (d) => {
  if (!d) return ''
  return new Date(d).toLocaleDateString('es-AR')
}

const getDayName = (d) => {
  if (!d) return ''
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
  return days[new Date(d).getDay()]
}
</script>

<template>
  <div class="lista-wrap">
    <div class="recordatorio-banner">
      <i class="pi pi-bell" />
      <span>Recordá <strong>informar al final de cada salida</strong> lo que se trabajó</span>
    </div>

    <div class="salida-list">
      <div
        v-for="(s, idx) in salidas"
        :key="idx"
        class="salida-card"
        role="button"
        tabindex="0"
        @click="$emit('select', idx)"
        @keydown.enter="$emit('select', idx)"
      >
        <div class="card-top">
          <div class="card-date-row">
            <span class="card-day">{{ getDayName(s.date) }}</span>
            <span class="card-date">{{ getLocalDate(s.date) }}</span>
          </div>
          <span v-if="s.hour" class="card-hour">
            <i class="pi pi-clock" />
            {{ s.hour }} hs
          </span>
        </div>

        <ul class="card-info">
          <li v-if="s.puntoEncuentro?.name || s.puntoEncuentro?.direccion" class="info-item">
            <span class="info-ico"><i class="pi pi-map-marker" /></span>
            <div class="info-body">
              <span class="info-label">Punto de encuentro</span>
              <span class="info-value">{{ s.puntoEncuentro.name || s.puntoEncuentro.direccion }}</span>
              <!-- <span v-if="s.puntoEncuentro.name && s.puntoEncuentro.direccion" class="info-sub">
                {{ s.puntoEncuentro.direccion }}
              </span> -->
            </div>
          </li>

          <li v-if="s.conductor" class="info-item">
            <span class="info-ico"><i class="pi pi-user" /></span>
            <div class="info-body">
              <span class="info-label">Conductor</span>
              <span class="info-value">{{ getConductorName(s.conductor) }}</span>
            </div>
          </li>

          <li v-if="s.territorios?.length" class="info-item">
            <span class="info-ico"><i class="pi pi-th-large" /></span>
            <div class="info-body">
              <span class="info-label">Territorio{{ s.territorios.length !== 1 ? 's' : '' }}</span>
              <div class="chips">
                <span v-for="t in s.territorios" :key="t" class="chip">{{ t }}</span>
              </div>
            </div>
          </li>
        </ul>

        <div v-if="s.gruposInvitadosData?.length" class="invitados-block">
          <div class="invitados-title">
            <i class="pi pi-users" />
            Grupos de invitados
          </div>
          <div
            v-for="g in s.gruposInvitadosData"
            :key="g.id"
            class="invitado-card"
          >
            <div v-if="g.encargado" class="encargado-row">
              <i class="pi pi-user-edit meta-icon" />
              <span class="encargado-name">{{ g.encargado }}</span>
            </div>
            <div class="invitado-detalle">
              <i class="pi pi-user meta-icon" />
              {{ g.cantidad }} persona{{ g.cantidad !== 1 ? 's' : '' }}
            </div>
            <div v-if="g.autos" class="invitado-detalle">
              <i class="pi pi-car meta-icon" />
              {{ g.autos }} vehículo{{ g.autos !== 1 ? 's' : '' }}
            </div>
            <div v-if="g.codigo" class="codigo-inline">
              Código: {{ g.codigo }}
            </div>
          </div>
        </div>

        <div class="card-actions">
          <button
            class="action-btn action-ver"
            @click.stop="$emit('select', idx, 'territorio')"
          >
            <i class="pi pi-map" />
            Ver territorio
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.lista-wrap {
  padding: 16px;
  max-width: 520px;
  margin: 0 auto;
}

.recordatorio-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
  border-radius: var(--radius-md);
  background: var(--success-soft);
  color: var(--success-deep);
  font-size: 15px;
  line-height: 1.35;
}

.recordatorio-banner .pi {
  flex-shrink: 0;
  font-size: 1.1rem;
}

.salida-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}

.salida-card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: 18px;
  cursor: pointer;
  box-shadow: var(--shadow-1);
  transition: transform var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
  -webkit-tap-highlight-color: transparent;
}

.salida-card:hover {
  box-shadow: var(--shadow-2);
}

.salida-card:active {
  transform: scale(0.99);
}

.salida-card:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 16px;
}

.card-date-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.card-day {
  font-size: 22px;
  font-weight: var(--weight-bold);
  color: var(--label-primary);
  letter-spacing: -0.3px;
}

.card-date {
  font-size: 16px;
  color: var(--label-secondary);
}

.card-hour {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 5px 11px;
  border-radius: var(--radius-pill);
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 16px;
  font-weight: var(--weight-semibold);
}

.card-hour .pi {
  font-size: 0.8rem;
}

/* ---- Datos de la salida ---- */
.card-info {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.info-ico {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  background: var(--fill-quaternary);
  color: var(--label-secondary);
}

.info-ico .pi {
  font-size: 0.85rem;
}

.info-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.info-label {
  font-size: 12px;
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--label-secondary);
}

.info-value {
  font-size: 17px;
  color: var(--label-primary);
  overflow-wrap: anywhere;
}

.info-sub {
  font-size: 14px;
  color: var(--label-secondary);
  overflow-wrap: anywhere;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.chip {
  padding: 3px 10px;
  border-radius: var(--radius-pill);
  background: var(--fill-quaternary);
  color: var(--label-primary);
  font-size: 15px;
  font-weight: var(--weight-semibold);
  font-variant-numeric: tabular-nums;
}

/* ---- Grupos de invitados / código de acceso ---- */
.invitados-block {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--separator);
}

.invitados-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: var(--weight-semibold);
  color: var(--label-secondary);
  margin-bottom: 10px;
}

.invitado-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: var(--radius-md);
  background: var(--bg-secondary);
  margin-bottom: 8px;
}

.invitado-card:last-child {
  margin-bottom: 0;
}

.encargado-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}

.encargado-row .meta-icon {
  font-size: 0.9rem;
  color: var(--accent);
}

.encargado-name {
  font-size: 18px;
  font-weight: var(--weight-bold);
  color: var(--label-primary);
}

.invitado-detalle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  color: var(--label-primary);
}

.invitado-detalle .meta-icon {
  font-size: 0.8rem;
  color: var(--label-secondary);
}

.codigo-inline {
  font-size: 13px;
  color: var(--label-secondary);
  margin-top: 2px;
}

/* ---- Botón de acción ---- */
.card-actions {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--separator);
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: none;
  border-radius: var(--radius-md);
  font-size: 16px;
  font-weight: var(--weight-semibold);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform var(--duration-fast) var(--ease-standard),
    filter var(--duration-fast) var(--ease-standard);
}

.action-btn:active {
  transform: scale(0.97);
  filter: brightness(0.92);
}

.action-btn .pi {
  font-size: 1rem;
}

.action-ver {
  background: var(--accent-soft);
  color: var(--accent);
}
</style>
